import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const AXIS = { fontSize: 11, fill: "#8AB5B5" };

/* eslint-disable @typescript-eslint/no-explicit-any */
function ChartTooltip(props: any) {
  const { active, payload, label, suffix } = props;
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div dir="rtl" className="rounded-xl border border-[#E4F5F5] bg-white px-3 py-2 shadow-card">
      <p className="mb-1.5 text-xs font-semibold text-ghasla-text-heading">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="flex items-center gap-2 text-xs text-ghasla-text-primary">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color || p.fill }} />
          <span className="whitespace-nowrap">{p.name}:</span>
          <span className="font-semibold text-ghasla-text-heading">
            {typeof p.value === "number" ? p.value.toLocaleString("en-US") : p.value}
            {suffix ?? ""}
          </span>
        </p>
      ))}
    </div>
  );
}

interface DualLineProps {
  data: any[];
  xKey: string;
  leftKey: string;
  rightKey: string;
  leftName: string;
  rightName: string;
  leftColor?: string;
  rightColor?: string;
  height?: number;
}

export function DualAxisLineChart({
  data,
  xKey,
  leftKey,
  rightKey,
  leftName,
  rightName,
  leftColor = "#00AAB7",
  rightColor = "#27AE60",
  height = 300,
}: DualLineProps) {
  return (
    <div dir="ltr" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EAF5F5" vertical={false} />
          <XAxis dataKey={xKey} tick={AXIS} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis yAxisId="left" tick={AXIS} axisLine={false} tickLine={false} width={46} />
          <YAxis yAxisId="right" orientation="right" tick={AXIS} axisLine={false} tickLine={false} width={34} />
          <Tooltip content={<ChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey={leftKey}
            name={leftName}
            stroke={leftColor}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey={rightKey}
            name={rightName}
            stroke={rightColor}
            strokeWidth={2.5}
            strokeDasharray="6 4"
            dot={false}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SimpleLineChart({
  data,
  xKey,
  yKey,
  name,
  color = "#00AAB7",
  suffix = "",
  height = 260,
}: {
  data: any[];
  xKey: string;
  yKey: string;
  name: string;
  color?: string;
  suffix?: string;
  height?: number;
}) {
  return (
    <div dir="ltr" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EAF5F5" vertical={false} />
          <XAxis dataKey={xKey} tick={AXIS} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS} axisLine={false} tickLine={false} width={38} />
          <Tooltip content={<ChartTooltip suffix={suffix} />} />
          <Line
            type="monotone"
            dataKey={yKey}
            name={name}
            stroke={color}
            strokeWidth={2.5}
            dot={{ r: 3.5, fill: "#fff", stroke: color, strokeWidth: 2 }}
            activeDot={{ r: 5.5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface StackedProps {
  data: any[];
  xKey: string;
  keys: { key: string; name: string; color: string }[];
  height?: number;
}

export function StackedBarChart({ data, xKey, keys, height = 300 }: StackedProps) {
  return (
    <div dir="ltr" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EAF5F5" vertical={false} />
          <XAxis dataKey={xKey} tick={AXIS} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS} axisLine={false} tickLine={false} width={38} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "#F0FDFD" }} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          {keys.map((k, i) => (
            <Bar
              key={k.key}
              dataKey={k.key}
              name={k.name}
              stackId="a"
              fill={k.color}
              radius={i === keys.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
              maxBarSize={44}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function HBarList({
  items,
  color = "#00AAB7",
  suffix = "",
}: {
  items: { label: string; value: number }[];
  color?: string;
  suffix?: string;
}) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="space-y-4">
      {items.map((it, idx) => (
        <div key={it.label}>
          <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-medium text-ghasla-text-heading">
              {idx + 1}. {it.label}
            </span>
            <span className="whitespace-nowrap text-ghasla-text-primary">
              {it.value.toLocaleString("en-US")}
              {suffix}
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#F0FDFD]">
            <div
              className="h-full rounded-full transition-[width] duration-700"
              style={{ width: `${(it.value / max) * 100}%`, backgroundColor: color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SliceLegend({
  items,
  suffix = "%",
}: {
  items: { label: string; pct: number; color: string }[];
  suffix?: string;
}) {
  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
      {items.map((s) => (
        <span key={s.label} className="flex items-center gap-2 text-xs text-ghasla-text-secondary">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
          <span className="whitespace-nowrap">
            {s.label} {s.pct}
            {suffix}
          </span>
        </span>
      ))}
    </div>
  );
}